import { type ReactNode, createContext, useContext, useMemo } from "react";

/**
 * Every user-facing string the shared components draw on their own — button
 * captions, aria-labels, the pagination summary — in ONE typed object.
 *
 * `packages/ui` stays translation-free (CLAUDE.md § i18n): it never imports an
 * i18n library. An app builds this object from its own translations once, at
 * the root, and hands it to `<UiStringsProvider>`; every component reads its
 * defaults from here. An explicit prop on a component still wins, so a table
 * that wants "1–25 of 320 users" instead of "… results" passes its own
 * `summary`. Without a provider the English defaults below apply.
 *
 * Functions, not templates, for anything with a number in it: the app decides
 * pluralisation and word order in its own language (i18next `_one`/`_other`),
 * and formats the numbers with its own locale via `formatNumber`.
 */

export interface PaginationRange {
	/** 1-based index of the first row on the page (0 when `total` is 0). */
	from: number;
	/** 1-based index of the last row on the page. */
	to: number;
	total: number;
}

export interface DataTableStrings {
	/** Header of the row-actions column. Platform default "Action". */
	actionHeader: string;
	errorTitle: string;
	retry: string;
	emptyTitle: string;
	/** After the count badge in the bulk bar: "selected". */
	selected: string;
	/** "{count} on other pages" — without the leading separator. */
	onOtherPages: (count: number) => string;
	clearSelection: string;
	columns: string;
	showColumns: string;
	/** Badge on a column the picker cannot hide. */
	columnAlways: string;
	showAllColumns: string;
	hiddenCount: (count: number) => string;
	selectRow: (row: number) => string;
	selectAllRows: string;
	deselectAllRows: string;
}

export interface PaginationStrings {
	/** aria-label on the <nav>. */
	label: string;
	/** The whole "1–25 of 320 results" line. Receives raw numbers; format them with `formatNumber`. */
	summary: (range: PaginationRange) => string;
	/** Shown instead of the summary when `total` is 0. */
	empty: string;
	rowsPerPage: string;
	firstPage: string;
	previousPage: string;
	nextPage: string;
	lastPage: string;
	/** aria-label for a numbered page button. */
	page: (page: number) => string;
	/**
	 * @deprecated Only backs the legacy `itemNoun` prop, which callers are
	 * migrating off (pass a per-table `labels.summary` instead). `noun` arrives
	 * already singular/plural.
	 */
	summaryWithNoun: (range: PaginationRange & { noun: string }) => string;
	/** @deprecated Legacy `itemNoun` only — "No {noun}". */
	emptyWithNoun: (noun: string) => string;
	/** `variant="list"` pager: "Showing 1 to 20 of 327". Raw numbers; format them. */
	showing: (range: PaginationRange) => string;
	/** `variant="list"` page-size option: "20 / page". */
	perPage: (pageSize: number) => string;
	/** Keyset pager when the total is not known (a reload deep in the list): "Showing 21 to 40". Raw numbers; format them. */
	showingNoTotal: (range: { from: number; to: number }) => string;
}

/** `ListToolbar` / `ListHeader` chrome. */
export interface ListStrings {
	/** Search input placeholder when the page passes none. */
	searchPlaceholder: string;
	/** aria-label of the search button (the search applies on Enter or this). */
	search: string;
	clearSearch: string;
	/** aria-label of the sort select. */
	sort: string;
	/** The chip that empties every chip and the search. */
	clearAll: string;
	/** aria-label of the chips row. */
	quickFilters: string;
}

export interface SelectStrings {
	placeholder: string;
	searchPlaceholder: string;
	noMatches: string;
	/** The trigger's × and the multi-select footer's Clear button. */
	clear: string;
	/** aria-label for a chip's remove ×. */
	removeOption: (label: string) => string;
	/** Footer count in multi mode ("3 selected"). */
	selectedCount: (count: number) => string;
	selectAll: string;
}

export interface FilterBarStrings {
	/** aria-label on the filter <form>. */
	label: string;
	apply: string;
	reset: string;
}

export interface ModalStrings {
	/** aria-label on the × in the header. */
	close: string;
}

export interface UiStrings {
	/** Every number the components print goes through this. Default: the browser's locale. */
	formatNumber: (n: number) => string;
	dataTable: DataTableStrings;
	pagination: PaginationStrings;
	select: SelectStrings;
	filterBar: FilterBarStrings;
	modal: ModalStrings;
	list: ListStrings;
}

/** What a provider may pass: any subset, per section. Missing entries fall back to English. */
export interface UiStringsInput {
	formatNumber?: (n: number) => string;
	dataTable?: Partial<DataTableStrings>;
	pagination?: Partial<PaginationStrings>;
	select?: Partial<SelectStrings>;
	filterBar?: Partial<FilterBarStrings>;
	modal?: Partial<ModalStrings>;
	list?: Partial<ListStrings>;
}

const browserNumberFormat = new Intl.NumberFormat();
const defaultFormatNumber = (n: number) => browserNumberFormat.format(n);

/** The English copy the components have always shown, with numbers through `fmt`. */
export function englishUiStrings(fmt: (n: number) => string = defaultFormatNumber): UiStrings {
	return {
		formatNumber: fmt,
		dataTable: {
			actionHeader: "Action",
			errorTitle: "Couldn't load this list",
			retry: "Try again",
			emptyTitle: "Nothing here yet",
			selected: "selected",
			onOtherPages: (n) => `${fmt(n)} on other pages`,
			clearSelection: "Clear",
			columns: "Columns",
			showColumns: "Show columns",
			columnAlways: "always",
			showAllColumns: "Show all",
			hiddenCount: (n) => `${fmt(n)} hidden`,
			selectRow: (row) => `Select row ${fmt(row)}`,
			selectAllRows: "Select all rows on this page",
			deselectAllRows: "Deselect all rows on this page",
		},
		pagination: {
			label: "Pagination",
			summary: ({ from, to, total }) => `${fmt(from)}–${fmt(to)} of ${fmt(total)}`,
			empty: "No results",
			rowsPerPage: "Rows per page",
			firstPage: "First page",
			previousPage: "Previous page",
			nextPage: "Next page",
			lastPage: "Last page",
			page: (p) => `Page ${fmt(p)}`,
			summaryWithNoun: ({ from, to, total, noun }) => `${fmt(from)}–${fmt(to)} of ${fmt(total)} ${noun}`,
			emptyWithNoun: (noun) => `No ${noun}`,
			showing: ({ from, to, total }) => `Showing ${fmt(from)} to ${fmt(to)} of ${fmt(total)}`,
			perPage: (n) => `${fmt(n)} / page`,
			showingNoTotal: ({ from, to }) => `Showing ${fmt(from)} to ${fmt(to)}`,
		},
		select: {
			placeholder: "Select…",
			searchPlaceholder: "Search…",
			noMatches: "No matches",
			clear: "Clear",
			removeOption: (label) => `Remove ${label}`,
			selectedCount: (n) => `${fmt(n)} selected`,
			selectAll: "Select all",
		},
		filterBar: { label: "Filters", apply: "Filter", reset: "Reset" },
		modal: { close: "Close" },
		list: {
			searchPlaceholder: "Search",
			search: "Search",
			clearSearch: "Clear search",
			sort: "Sort",
			clearAll: "Clear all",
			quickFilters: "Quick filters",
		},
	};
}

/** Fill every gap in `input` from the English defaults (which then format with the input's `formatNumber`). */
export function resolveUiStrings(input: UiStringsInput = {}): UiStrings {
	const base = englishUiStrings(input.formatNumber ?? defaultFormatNumber);
	return {
		formatNumber: base.formatNumber,
		dataTable: { ...base.dataTable, ...defined(input.dataTable) },
		pagination: { ...base.pagination, ...defined(input.pagination) },
		select: { ...base.select, ...defined(input.select) },
		filterBar: { ...base.filterBar, ...defined(input.filterBar) },
		modal: { ...base.modal, ...defined(input.modal) },
		list: { ...base.list, ...defined(input.list) },
	};
}

/** `{ a: undefined }` must not overwrite a default — spread would. */
export function defined<T extends object>(o: T | undefined): Partial<T> {
	if (!o) return {};
	const out: Partial<T> = {};
	for (const [k, v] of Object.entries(o)) if (v !== undefined) (out as Record<string, unknown>)[k] = v;
	return out;
}

const UiStringsContext = createContext<UiStrings>(englishUiStrings());

/**
 * Provide the app's translated strings once, near the root. Re-render it with
 * a new `value` when the language changes (memoise the object on the language
 * so the tree does not re-render on every parent render).
 */
export function UiStringsProvider({ value, children }: { value: UiStringsInput; children: ReactNode }) {
	const resolved = useMemo(() => resolveUiStrings(value), [value]);
	return <UiStringsContext.Provider value={resolved}>{children}</UiStringsContext.Provider>;
}

/** The complete strings object for the current subtree — English when no provider is mounted. */
export function useUiStrings(): UiStrings {
	return useContext(UiStringsContext);
}
